import React, { useState, useEffect } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Checkbox,
  Chip,
  FormControlLabel,
  Grid,
  MenuItem,
  Select,
  Typography,
  useTheme,
} from '@mui/material';
import Box from '@mui/system/Box';
import { makeStyles } from 'tss-react/mui';
import { useDispatch, useSelector } from 'react-redux';
import { setAccessTokenSelector, setUserSelector } from 'redux/users/ducks';
import { isEqual } from 'lodash';
import { useTranslation } from 'react-i18next';
import { IOSSwitch } from 'pages/VehiclePage/TireConfig/TireConfig';
import ActionButtons from 'container/settings/customerSettings/form/ActionButtons';
import { getWorkForceData } from '../../../services/auth_service';
import {
  getSubscribedUsers,
  sendEmailToSelectedUsers,
} from '../../../services/fleet_service';
import { setNotification } from '../../../redux/notifications/ducks';
import { USER_ROLES } from '../../../utils/string_utils';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

const useStyles = makeStyles()(theme => {
  return {
    dayButton: {
      textTransform: 'unset',
      alignSelf: 'flex-end',
    },
    select: {
      '& .MuiSelect-selectMenu': {
        display: 'flex',
        flexWrap: 'wrap',
        gap: theme.spacing(1),
      },
    },
    infoBox: {
      padding: 10,
      boxShadow: '0 2px 2px rgba(0, 0, 0, 0.3)',
      borderRadius: 5,
      transition: 'boxShadow 0.3s ease-in-out',
      width: '100%',
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
      gap: 10,

      '&:hover': {
        boxShadow: '0 2px 2px rgba(0, 0, 0, 0.5)',
      },
    },
    textBody: {
      opacity: 0.6,
      fontSize: '12px',
    },
    textHeading: {
      fontSize: '16px',
    },
  };
});

const daysOfWeek = [
  { day: 'sunday' },
  { day: 'monday' },
  { day: 'tuesday' },
  { day: 'wednesday' },
  { day: 'thursday' },
  { day: 'friday' },
  { day: 'saturday' },
];

const Notifications = () => {
  const access_token = useSelector(setAccessTokenSelector);
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const [users, setUsers] = useState([]);
  const { classes } = useStyles();
  const [isExpanded, setIsExpanded] = useState({});
  const theme = useTheme();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const userDetails = useSelector(setUserSelector);
  const isAdmin =
    userDetails?.iam_role === USER_ROLES.admin ||
    userDetails?.iam_role === USER_ROLES.superAdmin;
  const [state, setState] = useState({});
  const [stateStatic, setStateStatic] = useState({});
  useEffect(() => {
    async function getData() {
      try {
        const response = await getWorkForceData();
        const { data } = await getSubscribedUsers();
        setStateStatic(JSON.parse(JSON.stringify(data?.resp)));
        // console.log('backend response', data?.resp);
        setState(data?.resp);
        const users = response?.data?.resp?.workforceData || [];
        setUsers(
          users.filter(
            user =>
              user.name !== 'CO2OPT BOT' &&
              user.iam_role !== 'admin' &&
              user.iam_role !== 'superAdmin' &&
              user.iam_role !== 'fleetManager'
          )
        );
      } catch (error) {
        console.error(error);
      }
    }
    if (access_token) {
      getData();
    }
  }, [access_token, isAdmin]);

  const getPayload = (selected, index) => {
    let payload = { user_subscription: [] };
    Object.keys(state).forEach(key => {
      if (!isEqual(state[key], stateStatic[key])) {
        payload.user_subscription.push({
          ...state[key],
          email_type: key,
        });
      }
    });

    return payload;
  };
  const handleUpdate = async () => {
    const payload = getPayload();
    setIsSubmitting(true);

    try {
      const response = await sendEmailToSelectedUsers(payload);
      if (response.status === 200) {
        dispatch(
          setNotification(
            'Email Subscription Updated Successfully',
            'success',
            true
          )
        );
        setStateStatic(JSON.parse(JSON.stringify(state)));
      } else {
        dispatch(setNotification('Something went wrong..', 'error', true));
      }
    } catch (err) {
      dispatch(setNotification('Something went wrong..', 'error', true));
      console.error(err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const selectUserChangeHandler = (key, id) => {
    const updatedState = { ...state };
    let userIds = updatedState[key].user_ids;
    if (userIds.includes(id)) {
      userIds = userIds.filter(user => user !== id);
    } else {
      userIds.push(id);
    }
    updatedState[key].user_ids = userIds;
    setState(updatedState);
  };

  const switchChangeHandler = key => {
    const updatedState = { ...state };
    updatedState[key].is_enabled = !updatedState[key].is_enabled;
    // if (!updatedState[key].is_enabqled) {
    //   updatedState[key] = JSON.parse(JSON.stringify(stateStatic[key]));
    // }
    setState(updatedState);
  };

  const selectAllUsersChangeHandler = key => {
    const updatedState = { ...state };
    let userIds = updatedState[key].user_ids;
    if (userIds.length === users.length) {
      userIds = [];
    } else {
      userIds = users.map(user => user.user_id);
    }
    updatedState[key].user_ids = userIds;
    setState(updatedState);
  };

  const renderUpdateNotificationButton = () => {
    return (
      <Box width='100%' display='flex' flexDirection='column' marginTop={1}>
        <ActionButtons
          datahook='update-pref'
          handleUpdate={() => handleUpdate()}
          handleClear={() => {
            setState(JSON.parse(JSON.stringify(stateStatic)));
          }}
          disabled={isEqual(state, stateStatic) || isSubmitting}
          isSubmitting={isSubmitting}
        />
      </Box>
    );
  };

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Typography variant='h6' data-hook='notification_summary'>
          {t('settingsPage.notifications.heading')}
        </Typography>
      </Grid>
      {Object.entries(state || {}).map(([key, value], i) => {
        const allSelected = value?.user_ids?.length === users?.length;
        const someSelected =
          value?.user_ids?.length > 0 &&
          value?.user_ids?.length < users?.length;

        return (
          <Grid key={key} item xs={6}>
            <Box className={classes.infoBox}>
              <Box
                display='flex'
                justifyContent='space-between'
                alignItems='center'
                gap={'30px'}
              >
                <Box>
                  <Typography
                    variant='subtitle1'
                    className={classes.textHeading}
                  >
                    {t(`settingsPage.notifications.${key}`)}
                  </Typography>
                  <Typography variant='subtitle' className={classes.textBody}>
                    {t(`settingsPage.notifications.${key}_body`)}
                  </Typography>
                </Box>
                <FormControlLabel
                  control={
                    <IOSSwitch
                      name={key}
                      checked={value.is_enabled}
                      onChange={e => switchChangeHandler(key)}
                      data-hook={`switch-${key}`}
                    />
                  }
                />
              </Box>
              {value.is_enabled && (
                <>
                  {key !== 'TREAD_DEPTH_DISCREPANCY' && (
                    <Select
                      variant='standard'
                      className={classes.select}
                      value={value?.frequency?.type}
                      onChange={e => {
                        const updatedState = { ...state };
                        let frequency = updatedState[key].frequency;
                        frequency.type = e.target.value;
                        if (e.target.value !== 'weekly' && !!frequency.days) {
                          delete frequency.days;
                        }
                        updatedState[key].frequency = frequency;
                        setState(updatedState);
                      }}
                      multiple={false}
                      data-hook={`${key}-frequency-select`}
                    >
                      {[
                        'daily',
                        'weekly',
                        'monthly',
                        'biannually',
                        'quaterly',
                      ].map((type, i) => (
                        <MenuItem
                          key={type}
                          value={type}
                          data-hook={`frequency-${i}`}
                        >
                          {t(`common.frequency.${type}`)}
                        </MenuItem>
                      ))}
                    </Select>
                  )}

                  <Box
                    style={{
                      display: 'flex',
                      flexDirection: 'row',
                      gap: '30px',
                      zIndex: 9,
                      justifyContent: 'space-between',
                    }}
                  >
                    {key !== 'TREAD_DEPTH_DISCREPANCY' && (
                      <Box>
                        {value?.frequency?.type === 'weekly' && (
                          <>
                            <Select
                              variant='standard'
                              className={classes.select}
                              multiple
                              value={value?.frequency?.days || []}
                              data-hook={`${key}-day-select`}
                              onChange={e => {
                                const updatedState = { ...state };
                                let frequency = updatedState[key].frequency;
                                frequency.days = e.target.value;
                                updatedState[key].frequency = frequency;
                                setState(updatedState);
                              }}
                              renderValue={selected => (
                                <Typography className={classes.textBody}>
                                  {t('settingsPage.notifications.selectDay')}
                                </Typography>
                              )}
                            >
                              {daysOfWeek.map(({ day, icon }) => (
                                <MenuItem
                                  style={{
                                    backgroundColor:
                                      value?.frequency?.days &&
                                      value?.frequency?.days.includes(day) &&
                                      theme.palette.primary.main,
                                    color:
                                      value?.frequency?.days &&
                                      value?.frequency?.days.includes(day) &&
                                      '#fff',
                                  }}
                                  data-hook={`day-${day}`}
                                  key={day}
                                  value={day}
                                >
                                  {t(`settingsPage.notifications.days.${day}`)}
                                </MenuItem>
                              ))}
                            </Select>
                            <Box
                              display='flex'
                              alignItems='center'
                              flexWrap='wrap'
                              gap={'5px'}
                              marginTop={2}
                            >
                              {value?.frequency?.days &&
                                value?.frequency?.days.map(day => (
                                  <Chip
                                    key={day}
                                    label={t(
                                      `settingsPage.notifications.days.${day}`
                                    )}
                                    // data-hook={`chip-${hook}`}
                                    clickable
                                    onDelete={
                                      value?.frequency?.days.length > 1
                                        ? () => {
                                            const updatedState = { ...state };
                                            let frequency =
                                              updatedState[key].frequency;
                                            frequency.days =
                                              frequency.days.filter(
                                                d => d !== day
                                              );
                                            updatedState[key].frequency =
                                              frequency;
                                            setState(updatedState);
                                          }
                                        : null
                                    }
                                  />
                                ))}
                            </Box>
                          </>
                        )}
                      </Box>
                    )}
                    {!!value?.user_ids && (
                      <Accordion
                        style={{ height: 'max-content', width: '50%' }}
                        expanded={isExpanded[key]}
                      >
                        <AccordionSummary
                          aria-controls='panel1a-content'
                          id='panel1a-header'
                          expandIcon={<ExpandMoreIcon />}
                          onClick={() =>
                            setIsExpanded({
                              ...isExpanded,
                              [key]: !isExpanded[key] ?? true,
                            })
                          }
                        >
                          <Typography
                            variant='h6'
                            data-hook={`select-org-user-${i}`}
                          >
                            {t('settingsPage.notifications.selectUser')}
                          </Typography>
                        </AccordionSummary>
                        <Box>
                          <AccordionDetails
                            style={{ maxHeight: '200px', overflow: 'auto' }}
                          >
                            <FormControlLabel
                              label={t(`multiLayerReport.select`)}
                              control={
                                <Checkbox
                                  checked={allSelected}
                                  indeterminate={someSelected}
                                  data-hook={`select-all-users-${i}`}
                                />
                              }
                              onChange={e => selectAllUsersChangeHandler(key)}
                            />
                            <div>
                              <Box
                                sx={{
                                  display: 'flex',
                                  flexDirection: 'column',
                                  ml: 3,
                                  gap: 1,
                                  mt: 1,
                                }}
                              >
                                {users &&
                                  users.map((user, i) => {
                                    return (
                                      <FormControlLabel
                                        key={user?.user_id}
                                        label={user.name}
                                        control={
                                          <Checkbox
                                            data-hook={`org-user-${i}`}
                                            onChange={e =>
                                              selectUserChangeHandler(
                                                key,
                                                user.user_id
                                              )
                                            }
                                          />
                                        }
                                        checked={value?.user_ids?.includes(
                                          user.user_id
                                        )}
                                      />
                                    );
                                  })}
                              </Box>
                            </div>
                          </AccordionDetails>
                        </Box>
                      </Accordion>
                    )}
                  </Box>
                </>
              )}
            </Box>
          </Grid>
        );
      })}
      {renderUpdateNotificationButton()}
    </Grid>
  );
};

export default Notifications;
